import {
  mediaQueryLarge,
  requestIdleCallback,
  startViewTransition,
} from "@theme/utilities";
import PaginatedList from "@theme/paginated-list";
import { sectionRenderer } from "@theme/section-renderer";

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {boolean} */
  #isLoadingNextPage = false;

  /** @type {MutationObserver|null} */
  #gridObserver = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange);

    // Check if load more button is enabled in settings
    const enableLoadMore = this.dataset.enableLoadMore === "true";

    if (enableLoadMore) {
      this.#setupViewMoreButton();
      this.#configureHybridScroll();
      this.#observeGridChanges();
    }

    this.setAttribute("initialized", "");
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener("change", this.#handleMediaQueryChange);

    // Only remove view more button if it was enabled
    const enableLoadMore = this.dataset.enableLoadMore === "true";
    if (enableLoadMore) {
      this.#removeViewMoreButton();
    }

    // Disconnect grid observer
    if (this.#gridObserver) {
      this.#gridObserver.disconnect();
      this.#gridObserver = null;
    }

    // Reset loading state
    this.#isLoadingNextPage = false;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ["product-grid"]);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? "desktop" : "mobile";
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute("product-grid-view", value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout("default");
  };

  /**
   * Configure hybrid scroll: keep upward infinite scroll, disable forward scroll
   * This allows scrolling up to load previous pages while using Load More button for next pages
   */
  #configureHybridScroll() {
    const { viewMoreNext, viewMorePrevious } = this.refs;

    // Only disconnect the forward (next) observer
    // Keep backward (previous) observer active for upward infinite scroll
    if (viewMoreNext && this.infinityScrollObserver) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
    }
  }

  /**
   * Observe grid changes to update "Viewing" text when previous pages are loaded
   */
  #observeGridChanges() {
    const { grid } = this.refs;
    if (!grid) return;

    // Create observer to watch for new items being added to the grid
    this.#gridObserver = new MutationObserver((mutations) => {
      // Check if child nodes were added (new products loaded)
      const hasNewChildren = mutations.some(
        (mutation) =>
          mutation.type === "childList" && mutation.addedNodes.length > 0,
      );

      if (hasNewChildren) {
        // Update the view more button state after DOM settles
        requestAnimationFrame(() => {
          this.#updateViewMoreButtonState();
        });
      }
    });

    // Observe the grid for child additions
    this.#gridObserver.observe(grid, {
      childList: true,
      subtree: false, // Only watch direct children
    });
  }

  /**
   * Sets up the View More button functionality
   */
  #setupViewMoreButton() {
    const viewMoreButton = this.querySelector('[data-action="load-more"]');
    if (viewMoreButton) {
      viewMoreButton.addEventListener("click", this.#handleViewMoreClick);
      this.#updateViewMoreButtonState();
    }
  }

  /**
   * Removes the View More button event listeners
   */
  #removeViewMoreButton() {
    const viewMoreButton = this.querySelector('[data-action="load-more"]');
    if (viewMoreButton) {
      viewMoreButton.removeEventListener("click", this.#handleViewMoreClick);
    }
  }

  /**
   * Handles the View More button click
   */
  /**
   * @param {Event} event
   */
  #handleViewMoreClick = async (event) => {
    event.preventDefault();
    /** @type {HTMLButtonElement} */
    const button = /** @type {HTMLButtonElement} */ (event.target);

    if (!button) return;

    // Prevent concurrent requests
    if (this.#isLoadingNextPage) {
      return;
    }

    // Set loading state
    this.#isLoadingNextPage = true;

    // Disable button and show loading state
    button.disabled = true;
    const originalText = button.textContent || "VIEW MORE";
    button.textContent = "Loading...";

    try {
      // Call our manual page loading method
      await this.#loadNextPageManually();

      // Update button state after successful loading
      this.#updateViewMoreButtonState();
    } catch (error) {
      console.error("[ResultsList] Error loading more products:", error);

      // Reset button state on error
      button.textContent = "Try Again";
      button.disabled = false;

      // Auto-reset to original text after 3 seconds
      setTimeout(() => {
        if (button.textContent === "Try Again") {
          button.textContent = originalText;
        }
      }, 3000);
    } finally {
      // Always clear loading state
      this.#isLoadingNextPage = false;
    }
  };

  /**
   * Updates the View More button state based on available pages
   */
  #updateViewMoreButtonState() {
    const viewMoreButton = this.querySelector('[data-action="load-more"]');
    const loadMoreContainer = this.querySelector(".product-grid__load-more");

    if (!viewMoreButton || !loadMoreContainer) return;

    const { grid, cards } = this.refs;
    if (!grid || !cards || cards.length === 0) {
      /** @type {HTMLElement} */ (loadMoreContainer).style.display = "none";
      return;
    }

    // Get first and last card pages to calculate range
    const firstCard = cards[0];
    const lastCard = cards[cards.length - 1];
    if (!firstCard || !lastCard) return;

    const firstPage = Number(firstCard.dataset.page);
    const lastPage = Number(grid.dataset.lastPage);
    const currentLastPage = Number(lastCard.dataset.page);
    const totalItems =
      Number(grid?.dataset.totalItems) || Number(this.dataset.totalItems) || 0;
    const itemsPerPage = Number(this.dataset.itemsPerPage) || 24;

    // Calculate item range based on visible cards
    const startIndex = (firstPage - 1) * itemsPerPage + 1;
    let endIndex = currentLastPage * itemsPerPage;

    // Handle last page with fewer items
    if (endIndex > totalItems) {
      endIndex = totalItems;
    }

    // Always show the container
    /** @type {HTMLElement} */ (loadMoreContainer).style.display = "block";

    // Update the info text with correct item range
    /** @type {HTMLElement|null} */
    const infoText = loadMoreContainer.querySelector(
      ".product-grid__load-more-text-info",
    );
    if (infoText) {
      infoText.innerHTML = `Viewing ${startIndex}-${endIndex} <span>of ${totalItems} items</span>`;
      // Update data attributes for reference
      infoText.setAttribute("data-start-index", startIndex.toString());
      infoText.setAttribute("data-end-index", endIndex.toString());
    }

    if (currentLastPage < lastPage) {
      // More pages available
      /** @type {HTMLButtonElement} */ (viewMoreButton).disabled = false;
      viewMoreButton.textContent = "VIEW MORE";
    } else {
      // No more pages available
      /** @type {HTMLButtonElement} */ (viewMoreButton).disabled = true;
      viewMoreButton.setAttribute("style", "display: none;");
    }
  }

  /**
   * Load next page manually without using IntersectionObserver
   */
  async #loadNextPageManually() {
    // Direct approach - just fetch and append the next page
    await this.#fetchAndAppendNextPage();
  }

  async #fetchAndAppendNextPage() {
    const { grid, cards } = this.refs;
    if (!grid || !cards || cards.length === 0) {
      return;
    }

    const lastCard = cards[cards.length - 1];
    if (!lastCard) return;
    const currentPage = Number(lastCard.dataset.page);
    const nextPage = currentPage + 1;
    const lastPage = Number(grid.dataset.lastPage);

    if (nextPage > lastPage) {
      return;
    }

    // Build next page URL
    const url = new URL(window.location.href);
    url.searchParams.set("page", nextPage.toString());
    url.hash = "";

    try {
      // Use Section Rendering API for faster, smaller response
      const sectionHTML = await sectionRenderer.getSectionHTML(
        this.sectionId,
        false, // Don't use cache for new pages
        url,
      );

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, "text/html");

      // Find the product grid in the section
      const newGrid = doc.querySelector('[ref="grid"]');
      if (!newGrid) {
        console.warn("[ResultsList] No grid found in next page");
        return;
      }

      const newItems = newGrid.querySelectorAll('[ref="cards[]"]');
      if (newItems.length === 0) {
        console.warn("[ResultsList] No new items found in next page");
        return;
      }

      // Use DocumentFragment for efficient batch insertion
      const fragment = document.createDocumentFragment();
      newItems.forEach((item) => {
        fragment.appendChild(item);
      });

      grid.appendChild(fragment);

      // Update browser URL
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.set("page", nextPage.toString());
      cleanUrl.hash = "";
      history.pushState("", "", cleanUrl.toString());
    } catch (error) {
      console.error("[ResultsList] Failed to load next page:", error);
      throw error;
    }
  }
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}

// new change
